import { Subject, Subscription, debounceTime } from 'rxjs';
import { Comment } from './comment';
import { CoordinatorService } from './coordinator.service';
import { BinaryTreeNode } from './binary-tree-node';
import { BinaryTree } from './binary-tree';

import { ScriptAutoUser } from './script-auto-user';

let AUTOCOUNTER = 1;

export type ADOPT_DATA = Comment[] | Map<string, Map<string, string[]>>;
export type ADOPT_PARAMS = {
    ratingsMode?: 'replace' | 'if-empty';
    tagsMode?: 'replace' | 'if-empty' | 'merge';
    omitBtSet?: Set<string>;
    itmIdSet?: Set<string>;
};

export class AutoUser {
    id: string;
    private _isCurrentSnapshot = false;

    private _name: string;

    isActive = true;

    private _markForManifestStore$ = new Subject<void>();
    markForCheck$ = this._markForManifestStore$.pipe(debounceTime(1000));
    private _sLs$: Subscription[] = [];

    private _commentMap = new Map<string, Comment[]>();

    private _btMap = new Map<string, Map<string, string[]>>(); // itmId: Map<btId, ndId[]>

    constructor(
        public coords: CoordinatorService,
        {
            id,
            name,
            isCurrentSnapshot,
        }: { id?: string; name?: string; isCurrentSnapshot?: boolean } = {},
    ) {
        this.id = id || this.coords.uniqueId();
        this._name = (name && this._normalizeName(name)) || `AUTO ${AUTOCOUNTER++}`;
        this._isCurrentSnapshot = !!isCurrentSnapshot;

        this._sLs$.push(
            this.markForCheck$.subscribe(() => {
                this._manifestStore();
            }),
        );
    }

    private get _storeId() {
        return `autoUser_${this.id}`;
    }

    get icon() {
        return this.coords.icon('autouser');
    }

    submitBulkBt(xitmIdLs: string[], bt: BinaryTree, selectedNodesSet: Set<string>) {
        const ndIdLs = Array.from(selectedNodesSet);

        xitmIdLs.forEach((xitmId) => {
            this.submitXitmBtChange(xitmId, bt.id, ndIdLs);
        });
    }

    currentXitmBt(xitmId: string, binaryTreeId: string) {
        const ls: string[] = this._btMap.get(xitmId)?.get(binaryTreeId) || [];
        return ls;
    }

    submitXitmBtChange(xitmId: string, binaryTreeId: string, selectedNdIdLs: string[]) {
        let mp = this._btMap.get(xitmId);
        if (!mp) {
            mp = new Map<string, string[]>();
            this._btMap.set(xitmId, mp);
        }
        mp.set(binaryTreeId, selectedNdIdLs);
        this.coords.display$.markForCheck();
        this.markForManifestStore();
    }

    get btMap() {
        const finalMp = new Map<string, Map<string, string[]>>();
        this._btMap.forEach((map, itmId) => {
            const btMap = new Map<string, string[]>();
            finalMp.set(itmId, btMap);
            map.forEach((ndIdLs, btId) => {
                btMap.set(btId, Array.from(ndIdLs));
            });
        });
        return finalMp;
    }

    editBTDialog(bt: BinaryTree) {
        this.coords.display$.dialog$.next({
            action: 'autouser-edit-bt',
            autouser: this,
            bt,
        });
    }

    private _manifestStore() {
        if (!this.coords.autouser$.has(this)) return;

        // Safety check: Never store currentAutouserSnapshot to manifest
        // It's a temporary snapshot for export only and should not persist
        if (this._isCurrentSnapshot) return;

        this.coords.manifest$.store({
            type: 'auto-user',
            id: this._storeId,
            valueMap: this.store,
        });
    }

    async delete() {
        this._sLs$.forEach((s) => s.unsubscribe());
        const resp = await this.coords.manifest$.delete([
            {
                id: this._storeId,
                type: 'auto-user',
            },
        ]);
        this.coords.autouser$.delete(this);
    }

    private _normalizeName(nm: string) {
        return nm.replaceAll(/\s+/g, ' ').trim();
    }

    get hasComments() {
        return !!this._commentMap.size;
    }

    get commentLs() {
        return Array.from(this._commentMap.values()).flat();
    }

    public get name(): string {
        return this._name;
    }
    public set name(value: string) {
        value = this._normalizeName(value);
        if (!value) return;
        this._name = value;
        this._manifestStore();
    }

    get activeBtLs() {
        const ls: BinaryTree[] = [];
        const btIdSet = new Set<string>();
        this._btMap.forEach((idMp) => Array.from(idMp.keys()).forEach((btId) => btIdSet.add(btId)));
        btIdSet.forEach((btId) => {
            const bt = this.coords.binaryTree$.getTree(btId);
            bt && ls.push(bt);
        });
        return ls;
    }

    toProject() {
        this.coords.autouser$.add(this);
    }

    get inProject() {
        return this.coords.autouser$.has(this);
    }

    private _provideCommentData() {
        const ls: any[] = [];
        this._commentMap.forEach((cLs) => {
            cLs.forEach((c) => {
                ls.push(c);
            });
        });
        return ls;
    }

    private _provideBinaryTreeData() {
        return this._btMap;
    }

    provideData(tag: 'comment' | 'binary-tree') {
        switch (tag) {
            case 'comment':
                return this._provideCommentData();
            case 'binary-tree':
                return this._provideBinaryTreeData();
        }
    }

    private _clearBTtype(tp: 'ratings' | 'tags') {
        const deleteSet = new Set<string>();
        const skipSet = new Set<string>();

        this._btMap.forEach((idMp) => {
            Array.from(idMp.keys()).forEach((btId) => {
                if (deleteSet.has(btId)) idMp.delete(btId);
                else if (skipSet.has(btId)) return;
                else {
                    const bt = this.coords.binaryTree$.getTree(btId);
                    if (bt && bt.type === tp) {
                        idMp.delete(btId);
                        deleteSet.add(btId);
                    } else skipSet.add(btId);
                }
            });
        });
    }

    clear(arg: string) {
        switch (arg) {
            case 'all':
                this._commentMap.clear();
                this._btMap.clear();
                break;
            case 'comments':
                this._commentMap.clear();
                break;
            case 'tags':
            case 'ratings':
                this._clearBTtype(arg);
                break;
            default:
                this._btMap.forEach((idMp) => {
                    console.log(idMp.delete(arg));
                });
        }
        this.coords.display$.markForCheck();
        this.markForManifestStore();
    }

    adoptData(arg: ADOPT_DATA, { tagsMode, ratingsMode, omitBtSet, itmIdSet }: ADOPT_PARAMS = {}) {
        const modeMap: Map<string, string> = new Map([
            ['ratings', 'if-empty'],
            ['tags', 'if-empty'],
        ]);

        ratingsMode && modeMap.set('ratings', ratingsMode);
        tagsMode && modeMap.set('tags', tagsMode);

        if (Array.isArray(arg)) {
            arg.forEach((origC: Comment) => {
                if (itmIdSet && !itmIdSet.has(origC.originId)) return;
                const c = new Comment(this.coords, {
                    text: origC.text,
                    originId: origC.originId,
                    ownerId: this.id,
                    autouserId: this.id,
                    date: origC.date.toString(),
                });
                this._addComment(c);
            });
        } else {
            arg.forEach((itmBtMap, itmId) => {
                if (itmIdSet && !itmIdSet.has(itmId)) return;
                let btIdMp = this._btMap.get(itmId);
                if (!btIdMp) {
                    btIdMp = new Map<string, string[]>();
                    this._btMap.set(itmId, btIdMp);
                }
                // if (mode !== 'merge') btIdMp.clear();
                itmBtMap.forEach((nodeLs, btId) => {
                    if (omitBtSet && omitBtSet.has(btId)) return;
                    const mode = modeMap.get(this.coords.binaryTree$.typeFromId(btId)!);
                    const originalLs = btIdMp.get(btId);
                    if (mode === 'if-empty' && originalLs && originalLs.length) return;
                    const ls = mode === 'merge' ? originalLs || [] : [];
                    ls.push(...nodeLs);
                    btIdMp.set(btId, Array.from(new Set(ls)));
                });
            });
        }
        this.markForManifestStore();
    }

    ofOriginCommentLs(originId: string) {
        const cLs = this._commentMap.get(originId);
        if (!cLs) return [];
        // console.log('AUTOUSER OF ORIGIN COMMENT LS', cLs);
        return cLs;
    }

    markForManifestStore() {
        this._markForManifestStore$.next();
    }

    private _addComment(comment: Comment) {
        let cLs = this._commentMap.get(comment.originId);
        if (!cLs) {
            cLs = [];
            this._commentMap.set(comment.originId, cLs);
        }
        cLs.push(comment);
    }

    load3(params: any) {
        // console.log('AUTOUSER LOAD3', params);
        params.comments?.forEach((cPar: any) => {
            cPar.autouserId = this.id;
            const c = new Comment(this.coords, cPar);
            this._addComment(c);
        });

        if (params.btValues) this._btMap = this.coords.utils$.objectsToMaps(params.btValues);
    }

    private _importBT(params: any) {
        const ls = [...(params.ratings || []), ...(params.tags || [])];
        ls.forEach((p: any) => {
            const bt = this.coords.binaryTree$.getTree(p.id);
            if (!bt) return;
            Object.keys(p.nodeItemIds || {}).forEach((nodeId: string) => {
                if (!bt.getNodeFromId(nodeId)) return;
                const itmIdLs: string[] = p.nodeItemIds[nodeId];

                itmIdLs.forEach((itmId) => {
                    if (!this.coords.dataset$.main.hasId(itmId)) return;
                    let mp = this._btMap.get(itmId);
                    if (!mp) {
                        mp = new Map<string, string[]>();
                        this._btMap.set(itmId, mp);
                    }

                    let ndLs = mp.get(bt.id);
                    if (!ndLs) {
                        ndLs = [];
                        mp.set(bt.id, ndLs);
                    }
                    ndLs.push(nodeId);
                });
            });
        });

        this._btMap.forEach((mp) => {
            mp.forEach((ndIdLs, btId) => {
                mp.set(btId, Array.from(new Set(ndIdLs)));
            });
        });
    }

    private _importComments(params: any) {
        params.comments?.forEach((cPar: any) => {
            // Check if comment already exists in global comment store to avoid duplicates
            // Comments may have been imported from data.comments array
            const originMap = this.coords.comment$.getItemMap(cPar.originId);
            if (originMap) {
                const isDuplicate = Array.from(originMap.values()).some(
                    (existingC) =>
                        existingC.text === cPar.text &&
                        existingC.originId === cPar.originId &&
                        (existingC.autouserId === this.id || existingC.ownerId === this.id),
                );

                if (isDuplicate) {
                    return;
                }
            }

            // Also check if this comment already exists in this autouser's comment map
            const existingAutouserComments = this._commentMap.get(cPar.originId);
            if (existingAutouserComments) {
                const isDuplicateInAutouser = existingAutouserComments.some(
                    (existingC) => existingC.text === cPar.text,
                );
                if (isDuplicateInAutouser) {
                    return;
                }
            }

            cPar.ownerId = this.id;
            cPar.autouserId = this.id;
            const c = new Comment(this.coords, cPar);
            this._addComment(c);
        });
    }

    import(params: any) {
        this._importBT(params);
        this._importComments(params);
        this.coords.display$.markForCheck();
    }

    hasAuto(bt: BinaryTree | undefined) {
        if (!bt) return false;
        return !!this._btMap.size;
    }

    currentBt(bt: BinaryTree): { au: AutoUser; ndIdLs: string[] } | null {
        const btMp = this._btMap.get(this.coords.resultList$.activeItem!.id);
        if (!btMp) return null;
        const ls = btMp.get(bt.id);
        if (!ls) return null;
        return {
            au: this,
            ndIdLs: ls,
        };
    }

    activeIdSet(btn: BinaryTreeNode, s: Set<string>) {
        const btId = btn.binaryTree.id;
        this._btMap.forEach((mp, itmId) => {
            const ls = mp.get(btId);
            if (!ls) return;
            if (ls.includes(btn.id)) s.add(itmId);
        });
    }

    unselect(btId: string, nodeIdLs: string[], idLs: string[]) {
        const bt = this.coords.binaryTree$.getTree(btId);
        if (!bt) return;
        nodeIdLs.forEach((ndId) => {
            const nd = bt.getNodeFromId(ndId);
            if (!nd) return;
            const offLs: string[] = [nd.id];
            offLs.push(...nd.descendants.map((cNd) => cNd.id));

            idLs.forEach((id) => {
                let idMap = this._btMap.get(id);
                if (!idMap) return;
                const ndLs = idMap.get(btId);
                if (!ndLs) return;
                const s = new Set(ndLs);
                offLs.forEach((offId) => s.delete(offId));
                idMap.set(btId, Array.from(s));
            });
        });
    }

    select(btId: string, nodeIdLs: string[], idLs: string[]) {
        const bt = this.coords.binaryTree$.getTree(btId);
        if (!bt) return;

        const isRatings = bt.type === 'ratings';

        nodeIdLs.forEach((ndId) => {
            const nd = bt.getNodeFromId(ndId);
            if (!nd) return;
            const ancestorLs = nd.ancestors;
            const onLs: string[] = ancestorLs.map((pNd) => pNd.id);
            onLs.push(nd.id);
            const offLs: string[] = [];
            if (isRatings) {
                const mainNode = ancestorLs[ancestorLs.length - 1];
                bt.root.childLs.forEach((pNd) => {
                    if (pNd === mainNode) return;
                    offLs.push(pNd.id);
                    offLs.push(...pNd.descendants.map((cNd) => cNd.id));
                });
            }

            idLs.forEach((id) => {
                let idMap = this._btMap.get(id);
                if (!idMap) {
                    idMap = new Map<string, string[]>();
                    this._btMap.set(id, idMap);
                }

                const s = new Set(idMap.get(btId) || []);
                offLs.forEach((offId) => s.delete(offId));
                onLs.forEach((onId) => s.add(onId));
                idMap.set(btId, Array.from(s));
            });
        });

        this.markForManifestStore();
    }

    json() {
        const bt = this.btJSON();
        const dct: any = {
            name: this.name,
            comments: this.commentJSON,
            ratings: bt.ratings,
            tags: bt.tags,
        };
        this._isCurrentSnapshot && (dct.isCurrentSnapshot = true);

        return dct;
    }

    comment(txt: string, origins?: Set<string> | string[]) {
        if (!origins) origins = [this.coords.project$.currentId!];
        origins.forEach((origin) => {
            const c = new Comment(this.coords, {
                originId: origin,

                text: txt,
            });
            this._addComment(c);
        });
        this.markForManifestStore();
    }

    get data() {
        return {
            id: this.id,
            name: this.name,
            isCurrentSnapshot: this._isCurrentSnapshot,
        };
    }

    btJSON() {
        const ratings: any[] = [];
        const tags: any[] = [];

        const btMap = new Map<string, Map<string, string[]>>();
        const sourceBT = this._isCurrentSnapshot ? this.coords.binaryTree$.btMap : this._btMap;

        // console.log(6666, this._btMap);

        sourceBT.forEach((mp, itmId) => {
            mp.forEach((ndIdLs, btId) => {
                let btIdMp = btMap.get(btId);
                if (!btIdMp) {
                    btIdMp = new Map<string, string[]>();
                    btMap.set(btId, btIdMp);
                }
                ndIdLs.forEach((ndId) => {
                    let ls = btIdMp.get(ndId);
                    if (!ls) {
                        ls = [];
                        btIdMp.set(ndId, ls);
                    }
                    ls.push(itmId);
                });
            });
        });

        btMap.forEach((mp, btId) => {
            const bt = this.coords.binaryTree$.getTree(btId);
            if (!bt) return;

            const dct = {
                id: btId,
                title: bt.title,
                nodeItemIds: this.coords.utils$.toJSONready(mp),
            };

            if (bt.type === 'ratings') ratings.push(dct);
            else if (bt.type === 'tags') tags.push(dct);
        });

        return {
            ratings,
            tags,
        };
    }

    get isCurrentSnapshot() {
        return this._isCurrentSnapshot;
    }

    asSnapshot(isCurrent = false) {
        isCurrent && (this._isCurrentSnapshot = true);
    }

    get commentJSON() {
        if (this._isCurrentSnapshot)
            return this.coords.comment$.currentUserCommentLs.map((c) => c.save());
        return this._provideCommentData().map((c) => c.save());
    }

    get store() {
        return new Map<string, any>([
            ['autoUser', this.data],
            ['comments', this.commentJSON],
            ['btValues', this.coords.utils$.toJSONready(this._btMap)],
        ]);
    }

    get scriptClass() {
        return new ScriptAutoUser(this.coords, this);
    }
}
